import { createRoot } from 'react-dom/client';

// third party
import { BrowserRouter } from 'react-router-dom';
import { Provider } from 'react-redux';

// project imports
import * as serviceWorker from 'serviceWorker';
import App from 'App';
import './styles.css';

// Self-hosted fonts - proper formats (woff2, woff), font-display: swap, Safari/Chrome/Firefox compatible.
//
// These were previously pulled from fonts.googleapis.com via a render-blocking <link> in
// public/index.html. That made every page load wait on a third-party CDN: if it is slow or
// unreachable (a coach on a restricted network, or the prod box behind a filter) the panel
// stalls and then falls back to a system face. Serving them from our own bundle removes the
// external dependency entirely.
//
// Only the weights the type scale actually uses are imported. Montserrat and Playfair were
// in the old <link> but are not selectable anywhere in the panel, so they are gone.
import '@fontsource/noto-sans-arabic/400.css';
import '@fontsource/noto-sans-arabic/500.css';
import '@fontsource/noto-sans-arabic/600.css';
import '@fontsource/noto-sans-arabic/700.css';

// Roboto is the Latin default (see src/config.js fontFamily).
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/600.css';
import '@fontsource/roboto/700.css';

// Montserrat and Playfair are NOT theme fonts — they are the choices in the ReactQuill
// rich-text editor's OWN font picker (assets/scss/style.scss, the `.ql-font-*` rules), used
// for authored content: FAQs, About us, Privacy Policy, Terms. Dropping them because the
// Customization panel doesn't offer them silently reverts published content and the editor
// body to a fallback face. They must stay loaded.
import '@fontsource/montserrat/400.css';
import '@fontsource/montserrat/500.css';
import '@fontsource/montserrat/700.css';
import '@fontsource/playfair/400.css';
import '@fontsource/playfair/500.css';
import '@fontsource/playfair/700.css';

// Inter and Poppins are the two alternatives offered by the Customization font picker.
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';
import '@fontsource/poppins/400.css';
import '@fontsource/poppins/500.css';
import '@fontsource/poppins/600.css';
import '@fontsource/poppins/700.css';

// style + assets
import 'assets/scss/style.scss';
import store from 'redux/store';
import { isChunkError, reloadOnceForChunk, clearReloadGuardWhenHealthy } from 'utils/chunkReload';
// import config from './config';

// Chunk-load recovery after a deploy. The same helpers are used by
// AppErrorBoundary — see src/utils/chunkReload.js for why both are needed.
window.addEventListener('error', (e) => {
  if (isChunkError(e)) reloadOnceForChunk(e);
});
window.addEventListener('unhandledrejection', (e) => {
  if (isChunkError(e)) reloadOnceForChunk(e);
});
clearReloadGuardWhenHealthy();

// ==============================|| REACT DOM RENDER  ||============================== //

const container = document.getElementById('root');
const root = createRoot(container); // createRoot(container!) if you use TypeScript
root.render(
  <Provider store={store}>
    <BrowserRouter>
      <App />
    </BrowserRouter>
  </Provider>
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
